// ════════════════════════════════════════════════════════════════════════════
//  ManDana — containment, and the one upward pointer
//  ---------------------------------------------------------------------------
//  md-record.ts settled the shape and wrote down the reason: "A type field
//  cannot enforce a hierarchy — a pointer does… THERE IS EXACTLY ONE UPWARD
//  POINTER IN THIS APP." What it did not have was anything that enforced it.
//  `pid` could name a record that names you back, and every surface asking
//  "what is above this?" would then walk for ever.
//
//  CONTAINMENT IS A TREE; EVERYTHING ELSE IS A GRAPH. A record is SHOWN in
//  twenty places and LIVES in one. Cards on a desk, wikilinks, database rows
//  and backlinks are all the graph — unlimited, symmetric, and none of them a
//  parent. This file is only about the tree.
//
//  WHY THE TREE HAS TO BE ONE. "Delete this, and everything inside it" is a
//  sentence the app says BEFORE the deletion happens, with a count in it. With
//  more than one parent that sentence has to end "…unless something else still
//  holds them", which is a delete whose outcome depends on state the person
//  cannot see. With a cycle it is not a sentence at all, it is a loop.
//
//  RANKS, NOT A PAIR TABLE. Domain 0, Pursuit 1, Project 2, everything else 3.
//  A pair table for n kinds is n² decisions that drift; a rank is one number per
//  kind and the rule is read once, here.
//
//  `<=` AND NOT `<`, DELIBERATELY. A record may contain a sub-record and a desk
//  may contain a sub-desk. Strict `<` would close that door permanently for the
//  sake of a check nobody asked for. What `<=` still makes structurally
//  impossible is a project under a record — the shape that would make the blast
//  radius unanswerable.
//
//  AN UNKNOWN KIND IS A LEAF. Same stance md-record.ts takes on an unknown ref
//  type: a newer client's kind round-trips rather than being refused. Ranking it
//  as a leaf is the SAFE guess — it can be contained by anything and can promote
//  nothing above a project.
//
//  CONVERGENCE IS COMPUTED FROM THE CLAIMS, NEVER FROM WHO IS ASKING. Two
//  devices offline, each moving one record somewhere different, have to agree
//  afterwards without talking. So mdContainWins takes two claims and reads only
//  what is in them; `mdContainWins(a, b)` and `mdContainWins(b, a)` are the same
//  value, which is the property that stops two devices overwriting each other
//  for ever.
//
//  PURE. No DOM, no store, no clock. Every function here takes the lookup it
//  needs as an argument, which is why all of it proves without a device, a
//  deploy or a database — the stance the loop's release gate took first.
// ════════════════════════════════════════════════════════════════════════════

/** One device's claim about where a record lives. `pid` null is the root. */
interface MdContainClaim {
  pid: string | null;
  at: number;
}

/** Why a proposed containment was refused. */
type MdContainReason = 'malformed' | 'self' | 'rank' | 'cycle';

(function (g: any) {
  'use strict';

  /* The four altitudes. Everything absent from this map is a leaf, which is
     what makes adding a kind a no-op here rather than an edit. */
  const MD_CONTAIN_RANKS: Record<string, number> = {
    domain: 0,
    pursuit: 1,
    project: 2,
  };

  const MD_CONTAIN_LEAF_RANK = 3;

  /* A ceiling on how far up a chain is walked before it is called broken. A
     store repaired badly — or written by a build that had no check — can hold a
     loop, and a render asking "may this move?" must terminate whatever it finds
     rather than hang the surface. The number is generous: no real containment
     chain is four deep, let alone this. */
  const MD_CONTAIN_MAX_DEPTH = 512;

  /** How far down the page a kind sits. Unknown kinds are leaves. */
  function mdContainRank(type: unknown): number {
    if (typeof type !== 'string') return MD_CONTAIN_LEAF_RANK;
    const canonical = type === 'manifest' ? 'craft' : type;
    const rank = MD_CONTAIN_RANKS[canonical];
    return typeof rank === 'number' ? rank : MD_CONTAIN_LEAF_RANK;
  }

  /** May a parent of this kind contain a child of that kind? */
  function mdContainAllows(parentType: unknown, childType: unknown): boolean {
    return mdContainRank(parentType) <= mdContainRank(childType);
  }

  /* Both a ref object and a ref key are accepted everywhere below, because the
     callers are split: a store holds `pid` as a string, a surface holds the ref
     it was handed. Normalising once here is cheaper than every caller
     remembering which shape it has. */
  function keyOf(ref: unknown): string {
    if (typeof ref === 'string') {
      return g.mdRecordRefParse && g.mdRecordRefParse(ref) ? ref : '';
    }
    return g.mdRecordRefKey ? g.mdRecordRefKey(ref) : '';
  }

  function typeOf(ref: unknown): string {
    const parsed = typeof ref === 'string'
      ? (g.mdRecordRefParse ? g.mdRecordRefParse(ref) : null)
      : ref;
    const t = parsed && typeof parsed === 'object' ? (parsed as { type?: unknown }).type : null;
    return typeof t === 'string' ? t : '';
  }

  /** May `child` be placed under `parent`? `parent` null means the root.

      `parentOf` answers "what is above this key?" and is the only thing here
      that touches anything outside its arguments — handed in so the rule can be
      proved against a plain object. */
  function mdContainCheck(
    child: unknown,
    parent: unknown,
    parentOf: (key: string) => string | null,
  ): { ok: true } | { ok: false; reason: MdContainReason } {
    const childKey = keyOf(child);
    if (!childKey) return { ok: false, reason: 'malformed' };

    /* The root is always allowed, and asking for it is how a move OUT is
       expressed. A tree with no way out of a branch is a prison. */
    if (parent == null || parent === '') return { ok: true };

    const parentKey = keyOf(parent);
    if (!parentKey) return { ok: false, reason: 'malformed' };
    if (parentKey === childKey) return { ok: false, reason: 'self' };

    if (!mdContainAllows(typeOf(parent), typeOf(child))) return { ok: false, reason: 'rank' };

    /* The occurs-check: walk up from the PROPOSED parent and refuse if the
       child is already above it. Bounded, because the chain being walked may
       already be broken — see MD_CONTAIN_MAX_DEPTH. */
    const seen = new Set<string>([parentKey]);
    let at: string | null = parentKey;
    for (let hops = 0; hops < MD_CONTAIN_MAX_DEPTH; hops += 1) {
      const above: string | null = typeof parentOf === 'function' ? parentOf(at as string) : null;
      if (!above) return { ok: true };
      if (above === childKey) return { ok: false, reason: 'cycle' };
      /* A loop that does not involve the child is somebody else's problem and
         not a reason to refuse this move. Stopping is the honest answer: it
         cannot reach the child through a ring it is already going round. */
      if (seen.has(above)) return { ok: true };
      seen.add(above);
      at = above;
    }
    return { ok: true };
  }

  /** The chain above a record, nearest first, bounded and loop-safe. */
  function mdContainChain(ref: unknown, parentOf: (key: string) => string | null): string[] {
    const start = keyOf(ref);
    const out: string[] = [];
    if (!start) return out;
    const seen = new Set<string>([start]);
    let at: string = start;
    for (let hops = 0; hops < MD_CONTAIN_MAX_DEPTH; hops += 1) {
      const above = typeof parentOf === 'function' ? parentOf(at) : null;
      if (!above || seen.has(above)) return out;
      seen.add(above);
      out.push(above);
      at = above;
    }
    return out;
  }

  /** Which of two claims about one record's parent stands.

      Later wins. A tie is broken by the parent named rather than by the device
      naming it, so both sides compute the same answer with nothing to compare
      notes about. Null — the root — is a claim like any other and sorts as the
      empty string. */
  function mdContainWins(
    a: MdContainClaim | null,
    b: MdContainClaim | null,
  ): MdContainClaim | null {
    if (!a) return b || null;
    if (!b) return a;
    const at = (c: MdContainClaim) => (Number.isFinite(c.at) ? Number(c.at) : 0);
    if (at(a) !== at(b)) return at(a) > at(b) ? a : b;
    const key = (c: MdContainClaim) => (typeof c.pid === 'string' ? c.pid : '');
    return key(a) >= key(b) ? a : b;
  }

  /** Everything a delete reaches: the record and its containment subtree.

      Root first, depth first, in the order `childrenOf` gives — so a
      confirmation dialog and the deletion itself walk the same list in the same
      order. Loop-safe for the same reason mdContainCheck is. */
  function mdContainSubtree(root: unknown, childrenOf: (key: string) => string[]): string[] {
    const start = keyOf(root);
    if (!start) return [];
    const out: string[] = [];
    const seen = new Set<string>();
    const stack: string[] = [start];
    while (stack.length && out.length < MD_CONTAIN_MAX_DEPTH * 64) {
      const at = stack.pop() as string;
      if (seen.has(at)) continue;
      seen.add(at);
      out.push(at);
      const kids = typeof childrenOf === 'function' ? childrenOf(at) : [];
      if (!Array.isArray(kids)) continue;
      /* Reversed onto the stack so the walk comes out in the order the store
         listed them, which is the order a person will see counted. */
      for (let i = kids.length - 1; i >= 0; i -= 1) {
        const kid = kids[i];
        if (typeof kid === 'string' && kid && !seen.has(kid)) stack.push(kid);
      }
    }
    return out;
  }

  Object.assign(g, {
    MD_CONTAIN_RANKS, MD_CONTAIN_LEAF_RANK, MD_CONTAIN_MAX_DEPTH,
    mdContainRank, mdContainAllows, mdContainCheck, mdContainChain,
    mdContainWins, mdContainSubtree,
  });

  // CommonJS escape hatch, following md-record.ts and md-merge.ts.
  const mod = (globalThis as { module?: { exports?: unknown } }).module;
  if (mod && mod.exports) {
    mod.exports = {
      MD_CONTAIN_RANKS, MD_CONTAIN_LEAF_RANK, MD_CONTAIN_MAX_DEPTH,
      mdContainRank, mdContainAllows, mdContainCheck, mdContainChain,
      mdContainWins, mdContainSubtree,
    };
  }
})(typeof window !== 'undefined' ? window : globalThis);
